import React, { ReactElement } from "react";

import classnames from "classnames";

import { useWombatTheme } from "../../theme";
import { ButtonScale } from "../Button";

export type ButtonGroupDirection = "horizontal" | "vertical";

export interface ButtonGroupProps
  extends React.ComponentPropsWithoutRef<"div"> {
  scale?: ButtonScale;
  direction?: ButtonGroupDirection;

  children: ReactElement[];
}

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    {
      scale = "base",
      direction = "horizontal",
      className,
      children,
      ...divHTMLProps
    },
    ref
  ) => {
    const { theme } = useWombatTheme();

    const classNames = classnames(
      theme.buttonGroup.base,
      theme.buttonGroup.direction[direction],
      className
    );

    const firstChildClassName =
      direction === "horizontal" ? "rounded-l-md" : "rounded-t-md";
    const secondToLastChildClassName =
      direction === "horizontal" ? "-ml-px" : "-mt-px";
    const lastChildClassName =
      direction === "horizontal" ? "rounded-r-md" : "rounded-b-md";

    return (
      <div ref={ref} className={classNames} {...divHTMLProps}>
        {children.flat().map(
          (child, childIndex) =>
            React.isValidElement(child) &&
            React.cloneElement(child, {
              key: child.props?.key || childIndex,
              __group__: true,
              variant: child.props?.variant || "ghost",
              scale: scale,
              className: classnames(
                direction === "vertical" && "text-center",
                childIndex === 0 && firstChildClassName,
                childIndex > 0 && secondToLastChildClassName,
                childIndex === children.flat().length - 1 && lastChildClassName
              ),
            })
        )}
      </div>
    );
  }
);

export default React.memo(ButtonGroup);
